import React, { memo, useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardActions, 
  Typography, 
  IconButton, 
  Box,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudIcon from '@mui/icons-material/Cloud';

/**
 * Card component for displaying a saved race plan on the home screen
 */
const PlanCard = memo(({ plan, onEdit, onDelete, onClick }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Log the plan data for debugging
  console.log('PlanCard: Rendering plan:', plan.id, 
    'Name:', plan.formData?.raceName || 'Unnamed', 
    'isCloudStored:', !!plan.isCloudStored,
    'cloudUrl:', plan.cloudUrl);
  
  // Format the creation date
  const formatDate = (plan) => {
    // Try different date fields in order of preference
    const timestamp = plan.lastAccessed || plan.savedAt || plan.timestamp || plan.sharedAt;
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Unknown date';
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };
  
  // Get the race name safely
  const raceName = plan.formData?.raceName || 'Unnamed Race';
  
  // Get distance with unit safely
  const distance = plan.formData?.distance ? 
    `${plan.formData.distance} ${plan.formData.distanceUnit || ''}` : 
    '';
  
  // Parse date string in local timezone (avoid UTC conversion issues)
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };
  
  // Calculate days until race if date is set
  const getDaysUntilRace = () => {
    if (!plan.formData?.raceDate) return null;
    
    try {
      const raceDate = parseLocalDate(plan.formData.raceDate);
      if (!raceDate) return null;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      raceDate.setHours(0, 0, 0, 0);
      
      const diffTime = raceDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return null; // Past race
      } else if (diffDays === 0) {
        return 'Today!';
      } else if (diffDays === 1) {
        return 'Tomorrow!';
      } else if (diffDays <= 7) {
        return `In ${diffDays} days`;
      } else if (diffDays <= 30) {
        return `In ${diffDays} days`;
      } else {
        return `In ${Math.round(diffDays / 7)} weeks`;
      }
    } catch (error) {
      return null;
    }
  };
  
  const daysUntilRace = getDaysUntilRace();
  
  // Format race date for display
  const formatRaceDate = () => {
    if (!plan.formData?.raceDate) return null;
    
    try {
      const date = parseLocalDate(plan.formData.raceDate);
      if (!date) return null;
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return null;
    }
  };
  
  const raceDateDisplay = formatRaceDate();
  
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        },
        cursor: 'pointer',
        zIndex: 50
      }}
      onClick={onClick}
    >
      <CardContent sx={{ flexGrow: 1, pt: 2, pb: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom noWrap title={raceName}>
          {raceName}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {distance}
        </Typography>
        
        {raceDateDisplay && (
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, mb: 0.5 }}>
            📅 {raceDateDisplay}
            {daysUntilRace && (
              <Chip 
                label={daysUntilRace} 
                size="small" 
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {formatDate(plan)}
        </Typography>
        
        {plan.isCloudStored && (
          <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
            <Tooltip title="Stored in cloud">
              <CloudIcon color="primary" fontSize="small" />
            </Tooltip>
          </Box>
        )}
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        {!plan.readOnly && (
          <>
            <Tooltip title="Edit plan">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(plan);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Delete plan">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        
        <Tooltip title="View plan">
          <IconButton size="small" color="primary">
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Delete Race Plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "<strong>{raceName}</strong>"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              onDelete(plan.id);
            }} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the plan ID changes or if isCloudStored status changes
  return (
    prevProps.plan.id === nextProps.plan.id &&
    prevProps.plan.isCloudStored === nextProps.plan.isCloudStored
  );
});

export default PlanCard; 